import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type {
  OrgStatus, PlanDto, PlatformAuditDto, PlatformHealthDto, PlatformMeDto, PlatformOrgRow, PlatformSettingsDto
} from "@delta/shared";
import { useLang } from "../../i18n";
import { errText, fieldErrs, statusPill, Title, when } from "./common";
import { Dashboard, PackageSettingsFields, Packages } from "./Packages";
import { ActivityList, BusinessDetail } from "./BusinessDetail";
import { ResellerDetail, Resellers } from "./Resellers";
import { AddBusinessButton } from "./AddBusiness";
import { DemoSettings } from "./DemoSettings";
import { WebsiteSettings } from "./WebsiteSettings";
import { api, ApiFailure } from "../../lib/api";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/Toast";

// The Delta Platform (Super Admin) portal under /platform. It signs in separately from the business app (its own
// cookie and sessions) and only ever calls /api/platform, which shows organization status, owner contacts and usage
// counts: there is no screen, and no API, for a business's books, documents or customers.

interface PCtx { me: PlatformMeDto | null; loading: boolean; setMe(m: PlatformMeDto | null): void }
const Ctx = createContext<PCtx>({ me: null, loading: true, setMe: () => undefined });
const usePlatform = () => useContext(Ctx);

export function PlatformApp() {
  const [me, setMe] = useState<PlatformMeDto | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api<PlatformMeDto>("GET", "/platform/auth/me").then(setMe, () => setMe(null)).finally(() => setLoading(false)); }, []);
  return (
    <Ctx.Provider value={{ me, loading, setMe }}>
      <Routes>
        <Route path="login" element={<PlatformLogin />} />
        <Route path="" element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Frame><Dashboard /></Frame>} />
        <Route path="businesses" element={<Frame><Businesses /></Frame>} />
        <Route path="businesses/:id" element={<Frame><BusinessDetail /></Frame>} />
        <Route path="plans" element={<Frame><Packages /></Frame>} />
        <Route path="resellers" element={<Frame><Resellers /></Frame>} />
        <Route path="resellers/:id" element={<Frame><ResellerDetail /></Frame>} />
        <Route path="audit" element={<Frame><Audit /></Frame>} />
        <Route path="health" element={<Frame><Health /></Frame>} />
        <Route path="settings" element={<Frame><Settings /></Frame>} />
        <Route path="demo" element={<Frame><DemoSettings /></Frame>} />
        <Route path="website" element={<Frame><WebsiteSettings /></Frame>} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>
    </Ctx.Provider>
  );
}

function PlatformLogin() {
  const { t, lang, setLang } = useLang();
  const { me, setMe } = usePlatform();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { document.title = `${t("pfSignInTitle")} · Delta`; }, [t]);
  if (me) return <Navigate to="/platform/dashboard" replace />;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try { setMe(await api<PlatformMeDto>("POST", "/platform/auth/login", { email: email.trim(), password: pw })); nav("/platform/dashboard", { replace: true }); }
    catch (x) { setErr(x instanceof ApiFailure && x.code === "too_many_attempts" ? t("tooManyAttempts") : t("pfBadCredentials")); setPw(""); }
    finally { setBusy(false); }
  };
  return (
    <div className="pf-login">
      <form noValidate onSubmit={submit} data-testid="pf-login">
        <div className="pf-row" style={{ justifyContent: "space-between" }}><b>{t("pfName")}</b>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setLang(lang === "ar" ? "en" : "ar")}><Icon name="globe" />{t("switchLang")}</button></div>
        <h1>{t("pfSignInTitle")}</h1><p>{t("pfSignInLede")}</p>
        <div className="field"><label htmlFor="pf-e">{t("pfEmail")}</label><input id="pf-e" className="control" dir="ltr" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <div className="field"><label htmlFor="pf-p">{t("password")}</label><input id="pf-p" className="control" type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
        {err && <div className="dlg-alert show" role="alert" data-testid="pf-login-error"><Icon name="alert" /><span>{err}</span></div>}
        <button className="btn btn-primary" type="submit" disabled={busy}>{t("pfSignIn")}</button>
      </form>
    </div>
  );
}

function Frame({ children }: { children: ReactNode }) {
  const { t, lang, setLang } = useLang();
  const { me, loading, setMe } = usePlatform();
  const loc = useLocation();
  const nav = useNavigate();
  if (loading) return <div className="state-box"><p>{t("pfLoading")}</p></div>;
  if (!me) return <Navigate to="/platform/login" replace state={{ from: loc.pathname }} />;
  const links: [string, string][] = [["dashboard", "pfNavDashboard"], ["businesses", "pfNavBusinesses"], ["plans", "pfNavPlans"], ["resellers", "rsTitle"], ["audit", "pfNavAudit"], ["health", "pfNavHealth"], ["settings", "pfNavSettings"], ["demo", "pfNavDemo"], ["website", "pfNavWebsite"]];
  return (
    <div className="pf">
      <header className="pf-top">
        <span className="brand"><Icon name="shield" />{t("pfName")} <small>Super Admin</small></span>
        <nav className="pf-nav">{links.map(([p, k]) => <NavLink key={p} to={`/platform/${p}`} className={({ isActive }) => (isActive ? "on" : "")}>{t(k)}</NavLink>)}</nav>
        <button className="btn btn-sm" onClick={() => setLang(lang === "ar" ? "en" : "ar")}><Icon name="globe" />{t("switchLang")}</button>
        <button className="btn btn-sm" data-testid="pf-signout" onClick={async () => { await api("POST", "/platform/auth/logout").catch(() => undefined); setMe(null); nav("/platform/login"); }} title={me.email}>{t("pfSignOut")}</button>
      </header>
      <main className="pf-main">{children}<p className="pf-boundary"><Icon name="lock" />{t("pfBoundary")}</p></main>
    </div>
  );
}

// ---------------------------------------------------------------- businesses

// Filters live in the address (the dashboard's figures link here with the same conditions the figure counted).
const FILTERS = ["package", "expiring", "scheduled", "request", "paidRecorded", "blocked", "rules"] as const;

function Businesses() {
  const { t, L, lang } = useLang();
  const [sp, setSp] = useSearchParams();
  const status = sp.get("status") ?? "";
  const [q, setQ] = useState(sp.get("q") ?? "");
  const [rows, setRows] = useState<PlatformOrgRow[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const extra = FILTERS.filter((k) => sp.get(k));
  const key = sp.toString();
  useEffect(() => {
    const h = setTimeout(() => {
      // Only the list's own filters go to the server (the address may also carry ?lang=).
      const all = new URLSearchParams(key);
      const p = new URLSearchParams();
      for (const k of ["status", ...FILTERS]) { const x = all.get(k); if (x) p.set(k, x); }
      if (q.trim()) p.set("q", q.trim());
      setFailed(null);
      api<PlatformOrgRow[]>("GET", `/platform/organizations?${p}`).then(setRows, (e) => { setRows([]); setFailed(errText(t, e)); });
    }, 200);
    return () => clearTimeout(h);
  }, [key, q, t]);
  const setStatus = (v: string) => { const p = new URLSearchParams(sp); if (v) p.set("status", v); else p.delete("status"); setSp(p, { replace: true }); };
  const clear = () => { const p = new URLSearchParams(sp); FILTERS.forEach((k) => p.delete(k)); setSp(p, { replace: true }); };
  const filterText = extra.map((k) => k === "package" ? t(`pfPkg_${sp.get(k)}`) : k === "expiring" ? t("pfFilterExpiring", { n: sp.get(k) ?? "" }) : k === "rules" ? t(`pfRules_${sp.get(k)}`) : t(`pfFilter_${k}`)).join(" · ");
  const ver = (r: PlatformOrgRow) => (r.signup ? <span className="pf-small">{t("sstChannelSms")}: {r.signup.mobileVerified ? "✓" : "✗"} · {t("sstChannelEmail")}: {r.signup.emailVerified ? "✓" : "✗"}</span> : <span className="pf-small">--</span>);
  return (
    <>
      <div className="pf-row" style={{ justifyContent: "space-between" }}><Title text={t("pfNavBusinesses")} /><AddBusinessButton /></div>
      <section className="card list-card">
        <div className="pf-bar">
          <div className="segmented">{["", "pending", "active", "suspended", "rejected"].map((s) => (
            <label key={s}><input type="radio" name="pf-st" checked={status === s} onChange={() => setStatus(s)} /><span>{s ? t(`pfStatus_${s}`) : t("pfFilterAll")}</span></label>
          ))}</div>
          <input className="control" placeholder={t("pfSearch")} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t("pfSearch")} />
          {extra.length > 0 && <span className="pf-chip" data-testid="pf-filter">{filterText}<button className="btn btn-sm btn-ghost" aria-label={t("pfClearFilter")} title={t("pfClearFilter")} onClick={clear}><Icon name="x" /></button></span>}
          {rows && <span className="pf-small" data-testid="pf-count">{t("pfCount", { n: rows.length })}</span>}
        </div>
        <div className="pf-scroll">
          {failed ? <div className="state-box error"><Icon name="alert" /><h3>{failed}</h3></div> : !rows ? <div className="state-box"><p>{t("pfLoading")}</p></div> : !rows.length ? <div className="state-box"><p>{t("pfNoBusinesses")}</p></div> : (
            <table className="tbl" data-testid="pf-businesses"><thead><tr>
              <th>{t("pfColBusiness")}</th><th>{t("pfColStatus")}</th><th>{t("pfColVerification")}</th><th>{t("pfColOnboarding")}</th><th>{t("pfColPlan")}</th><th>{t("pfColUsage")}</th><th>{t("pfColCreated")}</th>
            </tr></thead><tbody>{rows.map((r) => (
              <tr key={r.id} data-org={r.id}>
                <td><Link className="inv-no" to={`/platform/businesses/${r.id}`}>{L(r)}</Link><div className="pf-small">{t(`pfOrigin_${r.origin}`)}{r.signup ? ` · ${t(`pfSignup_${r.signup.status}`)}` : ""}</div></td>
                <td>{statusPill(t, r.status)}</td><td>{ver(r)}</td>
                <td className="num">{r.onboarding ? (r.onboarding.completedAt ? t("obState_done") : `${r.onboarding.done} / ${r.onboarding.total}`) : "--"}</td>
                <td>{r.packageState && r.packageState !== "provisioned" && <span className={`pill ${r.packageState === "paid" ? "good" : r.packageState === "trial" ? "info" : r.packageState === "expired" ? "bad" : ""}`} data-pkg={r.packageState}>{t(`pfPkg_${r.packageState}`)}</span>}
                  {r.access && r.access !== "open" && <span className="pill bad" data-closed={r.access}>{t(`pfClosed_${r.access}`)}</span>}
                  {r.packageRules === "legacy" && <div className="pf-small">{t("pfRules_legacy")}</div>}
                  {r.subscription ? <div className="num pf-small">{r.subscription.planCode} · {r.subscription.endsOn}</div> : r.packageState === "provisioned" ? <span className="pf-small">{t("pfPkg_provisioned")}</span> : null}</td>
                <td className="num">{t("pfUsageShort", { u: r.usage.users, b: r.usage.branches })}</td>
                <td className="num">{when(r.createdAt, lang)}</td>
              </tr>
            ))}</tbody></table>
          )}
        </div>
      </section>
    </>
  );
}

// ---------------------------------------------------------------- audit, health, settings

function Audit() {
  const { t } = useLang();
  const [rows, setRows] = useState<PlatformAuditDto[] | null>(null);
  const [plans, setPlans] = useState<PlanDto[]>([]);
  useEffect(() => { api<PlatformAuditDto[]>("GET", "/platform/audit?limit=200").then(setRows, () => setRows([])); api<PlanDto[]>("GET", "/platform/plans").then(setPlans, () => undefined); }, []);
  return <><Title text={t("pfAuditTitle")} /><section className="card list-card">{rows ? <ActivityList rows={rows} plans={plans} showBusiness /> : <div className="state-box"><p>{t("pfLoading")}</p></div>}</section></>;
}

function Health() {
  const { t, lang } = useLang();
  const [h, setH] = useState<PlatformHealthDto | null>(null);
  useEffect(() => { api<PlatformHealthDto>("GET", "/platform/health").then(setH, () => undefined); }, []);
  if (!h) return <><Title text={t("pfHealthTitle")} /><div className="state-box"><p>{t("pfLoading")}</p></div></>;
  const prov = (s: string) => <span className={`pill ${s === "configured" ? "good" : "bad"}`}>{t(s === "configured" ? "pfConfigured" : "pfNotConfigured")}</span>;
  const up = h.uptimeSeconds > 3600 ? `${Math.floor(h.uptimeSeconds / 3600)} h` : `${Math.floor(h.uptimeSeconds / 60)} min`;
  return (
    <>
      <Title text={t("pfHealthTitle")} />
      <div className="pf-grid" data-testid="pf-health">
        <section className="card"><div className="card-head"><h2>{t("pfDatabase")}</h2><span className={`pill ${h.database.ok ? "good" : "bad"}`}>{h.database.ok ? "OK" : "!"}</span></div>
          <div className="pf-card-b"><dl className="pf-kv"><dt>{t("pfLatency")}</dt><dd className="num">{h.database.latencyMs} ms</dd><dt>{t("pfMigrations")}</dt><dd className="num">{h.database.migrations} · {h.database.latestMigration}</dd>
            <dt>{t("pfUptime", { x: "" }).trim()}</dt><dd className="num">{up}</dd></dl></div></section>
        <section className="card"><div className="card-head"><h2>{t("pfProviders")}</h2></div>
          <div className="pf-card-b"><dl className="pf-kv"><dt>SMS</dt><dd>{prov(h.providers.sms)}</dd><dt>Email</dt><dd>{prov(h.providers.email)}</dd><dt>{t("pfPayment")}</dt><dd>{prov(h.providers.payment)}</dd></dl></div></section>
        <section className="card"><div className="card-head"><h2>{t("pfSignupsQueue")}</h2><span className={`pill ${h.signups.mode === "closed" ? "" : h.signups.mode === "verified" ? "good" : "warn"}`} data-testid="pf-health-mode">{t(`pfModeShort_${h.signups.mode}`)}</span></div>
          <div className="pf-card-b"><dl className="pf-kv"><dt>{t("pfSignup_pending_verification")}</dt><dd className="num">{h.signups.pendingVerification}</dd><dt>{t("pfSignup_pending_review")}</dt><dd className="num">{h.signups.pendingReview}</dd>
            {(Object.keys(h.organizations) as OrgStatus[]).map((k) => <span key={k} style={{ display: "contents" }}><dt>{t(`pfStatus_${k}`)}</dt><dd className="num">{h.organizations[k]}</dd></span>)}</dl></div></section>
        <section className="card"><div className="card-head"><h2>{t("pfErrors")}</h2></div>
          <div className="pf-card-b"><span className="pf-small">{t("pfErrorsHint")}</span>
            {h.errors.length ? <table className="tbl"><tbody>{h.errors.map((e, i) => <tr key={i}><td className="num">{when(e.at, lang)}</td><td dir="ltr"><code>{e.method} {e.route}</code></td><td className="num">{e.status}</td><td dir="ltr">{e.code}</td></tr>)}</tbody></table>
              : <span>{t("pfNoErrors")}</span>}</div></section>
      </div>
    </>
  );
}

function Settings() {
  const { t } = useLang();
  const toast = useToast();
  const [s, setS] = useState<PlatformSettingsDto | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [plans, setPlans] = useState<PlanDto[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    api<PlatformSettingsDto>("GET", "/platform/settings").then(setS, (e) => setFailed(errText(t, e)));
    api<PlanDto[]>("GET", "/platform/plans").then(setPlans, () => undefined);
  }, [t]);
  if (failed) return <><Title text={t("pfSettingsTitle")} /><div className="state-box error"><Icon name="alert" /><h3>{failed}</h3></div></>;
  if (!s) return <><Title text={t("pfSettingsTitle")} /><div className="state-box"><p>{t("pfLoading")}</p></div></>;
  const keys: [keyof PlatformSettingsDto["noSubscription"], string][] = [["users", "pfMaxUsers"], ["branches", "pfMaxBranches"], ["warehouses", "pfMaxWarehouses"], ["treasuries", "pfMaxTreasuries"], ["posDevices", "pfMaxPos"]];
  const save = async () => {
    try { setS(await api("PUT", "/platform/settings", { signupMode: s.signupMode, noSubscription: s.noSubscription, noSubscriptionFeatures: s.noSubscriptionFeatures, signupOfferPlanId: s.signupOfferPlanId, expiryWarningDays: s.expiryWarningDays })); setErrs({}); toast(t("pfSaved")); }
    catch (e) { setErrs(fieldErrs(t, e)); toast(errText(t, e)); }
  };
  return (
    <>
      <Title text={t("pfSettingsTitle")} />
      <section className="card" data-testid="pf-settings"><div className="pf-card-b">
        <b>{t("pfSignupMode")}</b>
        <div className="pf-modes" role="radiogroup" aria-label={t("pfSignupMode")} data-testid="pf-signup-mode">
          {(["closed", "manual_review", "verified"] as const).map((m) => {
            const off = m === "verified" && (s.providers.sms !== "configured" || s.providers.email !== "configured");
            return (
              <label key={m} className={`pf-mode ${s.signupMode === m ? "on" : ""} ${off ? "off" : ""}`}>
                <input type="radio" name="pf-mode" value={m} checked={s.signupMode === m} disabled={off} onChange={() => setS({ ...s, signupMode: m })} />
                <span><b>{t(`pfModeShort_${m}`)}</b><span className="pf-small">{t(`pfMode_${m}`)}</span>
                  {off && <span className="pill bad">{t("pfModeVerifiedBlocked")}</span>}</span>
              </label>
            );
          })}
        </div>
        {s.signupMode === "manual_review" && <div className="dlg-alert show" role="note" data-testid="pf-manual-warning"><Icon name="alert" /><span>{t("pfModeManualWarn")}</span></div>}
        <b>{t("pfNoSubLimits")}</b><span className="pf-small">{t("pfNoSubHint")}</span>
        <div className="ob-form">{keys.map(([k, l]) => (
          <div key={k} className={`field ${errs[k] ? "invalid" : ""}`}><label htmlFor={`ps-${k}`}>{t(l)}</label>
            <input id={`ps-${k}`} className="control num" type="number" min={0} value={String(s.noSubscription[k])} onChange={(e) => setS({ ...s, noSubscription: { ...s.noSubscription, [k]: Number(e.target.value) } })} /><div className="ferr">{errs[k]}</div></div>
        ))}</div>
        <PackageSettingsFields s={s} setS={setS} plans={plans} errs={errs} />
        <div className="pf-row"><button className="btn btn-primary" data-testid="pf-settings-save" onClick={() => void save()}>{t("save")}</button></div>
      </div></section>
    </>
  );
}
